import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useLocation, Link } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import {
  selectSubscription, selectHasSubscription, selectIsSubscriptionActive,
  selectIsSubscriptionCanceled, selectCurrentPlan, selectPayments,
  selectPaymentsLoading, selectSubscriptionLoading, selectPortalLoading,
  selectCancelLoading, selectReactivateLoading,
} from '../../store/selectors/SubscriptionSelectors';
import { selectUser } from '../../store/selectors/AuthSelectors';
import {
  fetchMySubscription, fetchPayments, createPortalSession,
  cancelSubscription, reactivateSubscription,
} from '../../store/actions/SubscriptionActions';
import InvoiceService from '../../services/InvoiceService';
import { PageHeader, Card, Button, Badge, Dot, Table, Tr, Td, EmptyState } from '../ui';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A');
const fmtCurrency = (amt, cur = 'usd') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: cur.toUpperCase() }).format(amt / 100);

const payTone = (s) => (s === 'succeeded' ? 'good' : s === 'pending' ? 'warn' : s === 'failed' ? 'live' : 'neutral');
const payLabel = (s) => (s === 'succeeded' ? 'Paid' : s ? s[0].toUpperCase() + s.slice(1) : '—');

const Billing = ({
  subscription, hasSubscription, isActive, isCanceled, currentPlan, payments,
  paymentsLoading, subscriptionLoading, portalLoading, cancelLoading, reactivateLoading,
  fetchMySubscription, fetchPayments, createPortalSession, cancelSubscription, reactivateSubscription, currentUser,
}) => {
  const location = useLocation();
  const [showCancel, setShowCancel] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [initialLoad, setInitialLoad] = useState(true);
  const [invoices, setInvoices] = useState([]);

  useEffect(() => {
    if (new URLSearchParams(location.search).get('payment') === 'success') {
      setSuccess('Payment successful — your subscription is active and recorded below.');
      const t = setTimeout(() => setSuccess(null), 12000);
      return () => clearTimeout(t);
    }
  }, [location.search]);

  useEffect(() => {
    fetchMySubscription().catch(() => {}).finally(() => setInitialLoad(false));
    fetchPayments().catch(() => {});
    InvoiceService.myInvoices().then(setInvoices).catch(() => {});
  }, [fetchMySubscription, fetchPayments]);

  const managePayment = async () => {
    try {
      const res = await createPortalSession();
      if (res.url) window.open(res.url, '_blank');
    } catch { setError('Failed to open billing portal. Please try again.'); }
  };
  const doCancel = async () => {
    try {
      await cancelSubscription(true);
      setSuccess('Your subscription will cancel at the end of the current period.');
      setShowCancel(false);
      fetchMySubscription();
    } catch { setError('Failed to cancel subscription.'); }
  };
  const doReactivate = async () => {
    try {
      await reactivateSubscription();
      setSuccess('Your subscription has been reactivated.');
      fetchMySubscription();
    } catch { setError('Failed to reactivate subscription.'); }
  };

  if (initialLoad || subscriptionLoading) {
    return (
      <div className="tw-py-16 tw-text-center">
        <i className="fa-solid fa-spinner fa-spin tw-text-2xl tw-text-brand" />
        <p className="tw-mt-3 tw-text-content-muted">Loading subscription details…</p>
      </div>
    );
  }

  if (!hasSubscription) {
    return (
      <div className="tw-mx-auto tw-max-w-lg tw-py-8">
        {success && (
          <div className="tw-mb-4 tw-rounded-xl tw-border tw-border-good/30 tw-bg-good/10 tw-px-4 tw-py-3 tw-text-sm tw-text-good">{success}</div>
        )}
        <Card>
          <EmptyState
            icon="fa-credit-card"
            title="No active subscription"
            hint="Choose a plan to start broadcasting."
            action={<Button icon="fa-tags" onClick={() => { window.location.href = '/pricing'; }}>View Pricing</Button>}
          />
        </Card>
      </div>
    );
  }

  return (
    <>
      <PageHeader title="Billing & Subscription" subtitle="Manage your plan, payment method and invoices" />

      {error && <div className="tw-mb-4 tw-rounded-xl tw-border tw-border-live/30 tw-bg-live/10 tw-px-4 tw-py-3 tw-text-sm tw-text-live">{error}</div>}
      {success && <div className="tw-mb-4 tw-rounded-xl tw-border tw-border-good/30 tw-bg-good/10 tw-px-4 tw-py-3 tw-text-sm tw-text-good">{success}</div>}

      <div className="tw-grid tw-gap-6 lg:tw-grid-cols-3">
        <div className="lg:tw-col-span-2">
          <Card
            title="Current Plan"
            actions={
              isCanceled ? <Badge tone="warn">Canceling</Badge>
                : isActive ? <Badge tone="good"><Dot tone="good" /> Active</Badge>
                : <Badge tone="neutral">Inactive</Badge>
            }
          >
            <div className="tw-flex tw-items-start tw-justify-between">
              <div>
                <h3 className="tw-text-lg tw-font-bold tw-text-heading">{currentPlan?.name || 'Unknown Plan'}</h3>
                <p className="tw-text-sm tw-text-content-muted">{currentPlan?.description}</p>
              </div>
              <div className="tw-text-right">
                <span className="tw-text-xl tw-font-bold tw-text-heading">{fmtCurrency(currentPlan?.price_monthly || 0)}</span>
                <span className="tw-text-sm tw-text-content-faint">/mo</span>
              </div>
            </div>

            <div className="tw-my-4 tw-border-t tw-border-line" />

            <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2 tw-text-sm">
              <div>
                <div className="tw-text-content-faint"><i className="fa-solid fa-calendar tw-mr-1.5" />Current period</div>
                <div className="tw-mt-1 tw-text-content">{fmtDate(subscription?.current_period_start)} – {fmtDate(subscription?.current_period_end)}</div>
              </div>
              <div>
                <div className="tw-text-content-faint"><i className="fa-solid fa-credit-card tw-mr-1.5" />Payment method</div>
                <div className="tw-mt-1 tw-text-content">•••• •••• •••• 4242</div>
              </div>
            </div>

            {isCanceled && (
              <div className="tw-mt-4 tw-rounded-lg tw-border tw-border-warn/30 tw-bg-warn/10 tw-px-3 tw-py-2 tw-text-sm tw-text-warn">
                Your subscription ends {fmtDate(subscription?.current_period_end)}. Reactivate any time before then.
              </div>
            )}

            <div className="tw-mt-5 tw-flex tw-flex-wrap tw-gap-2">
              <Button variant="outline" icon="fa-arrow-up-right-from-square" onClick={managePayment} disabled={portalLoading}>
                Manage Payment Method
              </Button>
              {isCanceled ? (
                <Button variant="primary" icon="fa-rotate-right" onClick={doReactivate} disabled={reactivateLoading}>Reactivate</Button>
              ) : (
                <Button variant="danger" icon="fa-xmark" onClick={() => setShowCancel(true)} disabled={cancelLoading}>Cancel Subscription</Button>
              )}
            </div>
          </Card>
        </div>

        <Card title="Plan Features">
          <ul className="tw-space-y-2.5 tw-text-sm">
            {[
              ['Live streams', currentPlan?.max_streams || 0],
              ['Storage', `${currentPlan?.max_storage_gb || 0} GB`],
              ['Bandwidth', `${currentPlan?.max_bandwidth_gb || 0} GB`],
            ].map(([l, v]) => (
              <li key={l} className="tw-flex tw-justify-between">
                <span className="tw-text-content-muted">{l}</span>
                <strong className="tw-text-heading">{v}</strong>
              </li>
            ))}
          </ul>
          <Button variant="outline" className="tw-mt-4 tw-w-full" onClick={() => { window.location.href = '/pricing'; }}>Change Plan</Button>
        </Card>

        <div className="lg:tw-col-span-3">
          <Card title="Invoices" subtitle="Issued by Siman Tech" noPad>
            {invoices.length === 0 ? (
              <EmptyState icon="fa-file-invoice" title="No invoices yet" hint="An invoice is generated and emailed after each payment." />
            ) : (
              <Table head={['Invoice', 'Date', 'Description', 'Total', 'Status', '']}>
                {invoices.map((inv) => (
                  <Tr key={inv.id}>
                    <Td className="tw-font-mono tw-text-xs tw-font-semibold tw-text-heading">{inv.invoice_number}</Td>
                    <Td className="tw-text-content-muted">{fmtDate(inv.issue_date)}</Td>
                    <Td>{(inv.line_items && inv.line_items[0]?.description) || 'Subscription'}</Td>
                    <Td className="tw-font-semibold tw-text-heading">{fmtCurrency(inv.total, inv.currency)}</Td>
                    <Td><Badge tone={inv.status === 'paid' ? 'good' : inv.status === 'issued' ? 'warn' : 'neutral'}>{inv.status}</Badge></Td>
                    <Td className="tw-text-right tw-whitespace-nowrap">
                      <Link to={`/billing/invoices/${inv.id}`}><Button variant="ghost" size="sm" icon="fa-eye" /></Link>
                      <Button variant="secondary" size="sm" icon="fa-file-pdf" as="a" href={InvoiceService.invoicePdfUrl(inv.id)} target="_blank" rel="noreferrer">PDF</Button>
                    </Td>
                  </Tr>
                ))}
              </Table>
            )}
          </Card>

          {payments.length > 0 && (
            <Card title="Payment History" noPad className="tw-mt-6">
              <Table head={['Date', 'Description', 'Amount', 'Status']}>
                {payments.map((p) => (
                  <Tr key={p.id}>
                    <Td className="tw-text-content-muted">{fmtDate(p.created_at)}</Td>
                    <Td>{p.description || p.plan_name || 'Subscription'}</Td>
                    <Td className="tw-font-semibold tw-text-heading">{fmtCurrency(p.amount, p.currency)}</Td>
                    <Td><Badge tone={payTone(p.status)}>{payLabel(p.status)}</Badge></Td>
                  </Tr>
                ))}
              </Table>
            </Card>
          )}
        </div>
      </div>

      <Modal show={showCancel} onHide={() => setShowCancel(false)} centered>
        <Modal.Header closeButton><Modal.Title>Cancel subscription</Modal.Title></Modal.Header>
        <Modal.Body>
          <p>Cancel your subscription? It stays active until <strong>{fmtDate(subscription?.current_period_end)}</strong>.</p>
          <ul className="tw-mt-2 tw-list-disc tw-pl-5 tw-text-sm tw-text-content-muted">
            <li>Reactivate any time before the end date</li>
            <li>No refund for the current billing period</li>
          </ul>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCancel(false)}>Keep Subscription</Button>
          <Button variant="danger" onClick={doCancel} disabled={cancelLoading}>{cancelLoading ? 'Cancelling…' : 'Confirm Cancel'}</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const mapStateToProps = (state) => ({
  subscription: selectSubscription(state),
  hasSubscription: selectHasSubscription(state),
  isActive: selectIsSubscriptionActive(state),
  isCanceled: selectIsSubscriptionCanceled(state),
  currentPlan: selectCurrentPlan(state),
  payments: selectPayments(state),
  paymentsLoading: selectPaymentsLoading(state),
  subscriptionLoading: selectSubscriptionLoading(state),
  portalLoading: selectPortalLoading(state),
  cancelLoading: selectCancelLoading(state),
  reactivateLoading: selectReactivateLoading(state),
  currentUser: selectUser(state),
});

export default connect(mapStateToProps, {
  fetchMySubscription, fetchPayments, createPortalSession, cancelSubscription, reactivateSubscription,
})(Billing);
